import { z } from 'zod';

import { BrandExperienceTypes, BrandReach, Industry } from './brand-marketing';
import { makeEndpoint } from '../common';
import { isValidCountryCode } from '../validator/countryCode';
import { isValidCurrencyCode } from '../validator/currencyCode';

/**
 * Channel enum for provider sign-up
 */
export enum ProviderSignUpChannel {
  WAY = 'way',
  INVITE_LINK = 'invite-link',
  CAMPAIGN = 'campaign',
}

/**
 * Request schema for public provider sign-up endpoint
 */
export const CreateProviderSignUpRequestSchema = z
  .object({
    fullName: z.string().min(1, 'Full name is required'),
    email: z.string().email('Invalid email address'),
    businessName: z.string().min(1, 'Business name is required'),
    website: z.string().url('Invalid website URL').optional().nullable(),
    industry: z.nativeEnum(Industry),
    reach: z.nativeEnum(BrandReach).optional().nullable(),
    experienceTypes: z.array(z.nativeEnum(BrandExperienceTypes)).optional().nullable(),
    description: z.string(),
    channel: z.nativeEnum(ProviderSignUpChannel),
    campaignId: z.string().uuid('Campaign ID must be a valid UUID').optional().nullable(),
    invitedByBrandId: z.string().uuid('Invited by brand ID must be a valid UUID').optional(),
    invitationId: z.string().uuid('Invitation ID must be a valid UUID').optional(),
    countryCode: z
      .string()
      .length(2, 'Country code must be 2 characters')
      .refine((code) => isValidCountryCode(code), {
        message: 'Unsupported country code',
      }),
    currency: z
      .string()
      .length(3, 'Currency code must be 3 characters')
      .refine((code) => isValidCurrencyCode(code), {
        message: 'Unsupported currency code',
      }),
  })
  .strict()
  .refine(
    (data) => {
      if (data.channel === ProviderSignUpChannel.CAMPAIGN) {
        // campaignId is required
        return data.campaignId !== null && data.campaignId !== undefined && data.campaignId !== '';
      }
      if (data.channel === ProviderSignUpChannel.INVITE_LINK) {
        // invitationId is required
        return data.invitationId !== null && data.invitationId !== undefined && data.invitationId !== '';
      }
      return true;
    },
    {
      message: 'Campaign ID is required when channel is campaign or invitation ID is required when channel is direct link',
      path: ['campaignId'],
    },
  );

export type CreateProviderSignUpRequest = z.infer<typeof CreateProviderSignUpRequestSchema>;

/**
 * Response schema for public provider sign-up endpoint
 */
export const ProviderSignUpResponseSchema = z.object({
  brandId: z.string().uuid(),
  userId: z.string().uuid(),
});

export type ProviderSignUpResponse = z.infer<typeof ProviderSignUpResponseSchema>;

/**
 * Endpoint definition for public provider sign-up
 */
export const CreateProviderSignUpEndpoint = makeEndpoint({
  method: 'POST',
  url: () => 'v1/providers/sign-up',
  responseSchema: ProviderSignUpResponseSchema,
  requestSchema: CreateProviderSignUpRequestSchema,
});

/**
 * Full FormData fields for provider sign-up (includes files).
 * Files are handled via multipart/form-data on the backend.
 * @see CreateProviderSignUpRequest for the JSON-serializable fields
 */
export interface ProviderSignUpFormDataFields extends CreateProviderSignUpRequest {
  logo: File;
  identityImage: File;
}
